import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Loader2, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { useAuthStore, type AuthUser } from '@/stores/auth';

interface LoginResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

interface Star {
  left: string;
  top: string;
  size: number;
  delay: string;
  duration: string;
  opacity: number;
}

interface Shoot {
  top: string;
  left: string;
  delay: string;
  duration: string;
  rotate: number;
}

function makeStars(count: number, seed: number): Star[] {
  let s = seed;
  const rng = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return Array.from({ length: count }, () => ({
    left: `${(rng() * 100).toFixed(2)}%`,
    top: `${(rng() * 100).toFixed(2)}%`,
    size: +(rng() * 2 + 0.6).toFixed(2),
    delay: `${(rng() * 4).toFixed(2)}s`,
    duration: `${(rng() * 2.5 + 2).toFixed(2)}s`,
    opacity: +(rng() * 0.7 + 0.3).toFixed(2),
  }));
}

function makeShoots(count: number, seed: number): Shoot[] {
  let s = seed;
  const rng = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  return Array.from({ length: count }, () => ({
    top: `${(rng() * 60).toFixed(1)}%`,
    left: `${(rng() * 60 + 10).toFixed(1)}%`,
    delay: `${(rng() * 12).toFixed(1)}s`,
    duration: `${(rng() * 1.5 + 1.5).toFixed(2)}s`,
    rotate: Math.round(rng() * 25 + 15),
  }));
}

export function LoginPage() {
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const stars = useMemo(() => makeStars(110, 7), []);
  const shoots = useMemo(() => makeShoots(3, 19), []);

  async function handle(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await api
        .post('auth/login', { json: { username: username.trim(), password } })
        .json<LoginResponse>();
      setSession(res.access_token, res.user);
      toast.success('登录成功', `欢迎回来,${res.user.display_name}`);
      const to = search.redirect && search.redirect.startsWith('/') ? search.redirect : '/radar';
      navigate({ to });
    } catch (err: unknown) {
      const detail = await extractDetail(err);
      toast.error('登录失败', detail);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070b1e] text-white">
      <style>{`
        @keyframes sf-twinkle {
          0%, 100% { opacity: var(--sf-base, .35); transform: scale(1); }
          50%      { opacity: 1; transform: scale(1.35); }
        }
        @keyframes sf-shoot {
          0%   { transform: translate3d(0,0,0) rotate(var(--sf-rot, 20deg)); opacity: 0; }
          8%   { opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translate3d(420px, 220px, 0) rotate(var(--sf-rot, 20deg)); opacity: 0; }
        }
        @keyframes sf-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-10px); }
        }
        @keyframes sf-ring {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes sf-aurora {
          0%, 100% { transform: translate3d(-10%, -5%, 0) scale(1); opacity: .55; }
          50%      { transform: translate3d(5%, 8%, 0)  scale(1.15); opacity: .75; }
        }
        .sf-star  { animation: sf-twinkle var(--sf-dur) ease-in-out infinite; animation-delay: var(--sf-delay); }
        .sf-shoot { animation: sf-shoot   var(--sf-dur) linear        infinite; animation-delay: var(--sf-delay); }
        .sf-float { animation: sf-float   6s            ease-in-out   infinite; }
        .sf-ring  { animation: sf-ring    18s           linear        infinite; }
        .sf-aurora{ animation: sf-aurora  14s           ease-in-out   infinite; }
        @media (prefers-reduced-motion: reduce) {
          .sf-star, .sf-shoot, .sf-float, .sf-ring, .sf-aurora { animation: none !important; }
        }
      `}</style>

      {/* —— 银河渐层 —— */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 80% at 20% 10%, rgba(124,77,255,.35), transparent 60%),' +
            'radial-gradient(70% 80% at 85% 25%, rgba(255,168,84,.22), transparent 60%),' +
            'radial-gradient(120% 90% at 50% 110%, rgba(8,12,40,1), transparent 60%)',
        }}
      />
      <div className="sf-aurora pointer-events-none absolute inset-0 mix-blend-screen"
        style={{
          background:
            'radial-gradient(40% 35% at 60% 40%, rgba(86, 156, 255, .35), transparent 70%),' +
            'radial-gradient(35% 30% at 30% 70%, rgba(255, 120, 180, .25), transparent 70%)',
        }}
      />

      {/* —— 星星层 —— */}
      <div className="pointer-events-none absolute inset-0">
        {stars.map((st, i) => (
          <span
            key={i}
            className="sf-star absolute rounded-full bg-white"
            style={{
              left: st.left,
              top: st.top,
              width: `${st.size}px`,
              height: `${st.size}px`,
              boxShadow: '0 0 6px rgba(255,255,255,.8)',
              ['--sf-base' as string]: String(st.opacity),
              ['--sf-delay' as string]: st.delay,
              ['--sf-dur' as string]: st.duration,
            }}
          />
        ))}
        {shoots.map((sh, i) => (
          <span
            key={`s-${i}`}
            className="sf-shoot absolute h-px w-24 origin-left rounded-full"
            style={{
              top: sh.top,
              left: sh.left,
              background:
                'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.95), rgba(124,77,255,.0))',
              filter: 'drop-shadow(0 0 4px rgba(255,255,255,.9))',
              ['--sf-rot' as string]: `${sh.rotate}deg`,
              ['--sf-delay' as string]: sh.delay,
              ['--sf-dur' as string]: sh.duration,
            }}
          />
        ))}
      </div>

      {/* —— 内容居中 —— */}
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-5xl items-center gap-10 md:grid-cols-[1.05fr_1fr]">
          {/* —— 左:Logo + 文案 —— */}
          <div className="hidden flex-col items-center text-center md:flex md:items-start md:text-left">
            <div className="relative">
              {/* 光环 */}
              <div
                className="sf-ring absolute inset-0 -z-10 rounded-full"
                style={{
                  background:
                    'conic-gradient(from 0deg, rgba(255,168,84,.0) 0%, rgba(255,168,84,.7) 25%, rgba(124,77,255,.0) 50%, rgba(124,77,255,.7) 75%, rgba(255,168,84,.0) 100%)',
                  filter: 'blur(14px)',
                  transform: 'scale(1.25)',
                }}
              />
              <div
                className="sf-float relative grid h-32 w-32 place-items-center rounded-full"
                style={{
                  background:
                    'radial-gradient(circle at 30% 30%, rgba(255,255,255,.18), rgba(255,255,255,0) 60%), rgba(255,255,255,.04)',
                  border: '1px solid rgba(255,255,255,.18)',
                  boxShadow:
                    '0 0 0 1px rgba(255,255,255,.06) inset, 0 30px 80px -20px rgba(124,77,255,.6), 0 0 60px rgba(255,168,84,.25)',
                }}
              >
                <img
                  src="/StarFox_avatar.png"
                  alt="StarFox"
                  className="h-24 w-24 select-none object-contain drop-shadow-[0_8px_30px_rgba(255,168,84,.45)]"
                  draggable={false}
                />
              </div>
            </div>

            <p className="mt-8 text-sm text-white/70">星与狐之夜 · 短剧 AI 聚合创作平台</p>
            <p className="mt-1 max-w-sm text-xs leading-relaxed text-white/45">
              热点雷达 · 编剧工作台 · 制作流水线 · 数字资产 — 一站搞定从灵感到上线
            </p>

            <div className="mt-6 flex items-center gap-2 text-[11px] text-amber-200/80">
              <Sparkles className="h-3.5 w-3.5" />
              当夜空亮起,你的下一部爆款剧本正在生成
            </div>
          </div>

          {/* —— 右:登录卡 —— */}
          <form
            onSubmit={handle}
            className="relative w-full rounded-2xl p-7 backdrop-blur-xl md:p-8"
            style={{
              background:
                'linear-gradient(160deg, rgba(255,255,255,.10), rgba(255,255,255,.04))',
              border: '1px solid rgba(255,255,255,.16)',
              boxShadow:
                '0 30px 80px -20px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.04) inset',
            }}
          >
            {/* 移动端 logo */}
            <div className="mb-5 flex flex-col items-center md:hidden">
              <div className="relative grid h-24 w-24 place-items-center rounded-full"
                style={{
                  background:
                    'radial-gradient(circle at 30% 30%, rgba(255,255,255,.2), rgba(255,255,255,0) 60%), rgba(255,255,255,.04)',
                  border: '1px solid rgba(255,255,255,.18)',
                  boxShadow: '0 0 40px rgba(255,168,84,.35)',
                }}>
                <img src="/StarFox_avatar.png" alt="StarFox" className="h-20 w-20 object-contain" />
              </div>
              <h1 className="mt-3 text-2xl font-bold text-white">StarFox</h1>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-200/70">welcome back</div>
              <h2 className="mt-1 text-2xl font-semibold text-white">登录 StarFox</h2>
            </div>

            <div className="mt-6 space-y-3">
              <div>
                <label className="mb-1.5 block text-[11px] tracking-wider text-white/55">账号</label>
                <Input
                  type="text"
                  placeholder="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  className="!h-11 border-white/15 bg-white/[0.06] text-white placeholder:text-white/35 focus:!border-amber-300/60 focus:!ring-amber-300/20"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] tracking-wider text-white/55">密码</label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="!h-11 border-white/15 bg-white/[0.06] text-white placeholder:text-white/35 focus:!border-amber-300/60 focus:!ring-amber-300/20"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group relative mt-6 flex h-11 w-full items-center justify-center overflow-hidden rounded-xl text-sm font-semibold text-[#1a1230] shadow-lg transition disabled:cursor-not-allowed disabled:opacity-70"
              style={{
                background:
                  'linear-gradient(120deg, #ffd591 0%, #ffb547 45%, #c8a4ff 100%)',
                boxShadow:
                  '0 10px 30px -8px rgba(255,168,84,.6), 0 0 0 1px rgba(255,255,255,.4) inset',
              }}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/70 to-transparent transition group-hover:translate-x-full"
                style={{ transitionDuration: '900ms' }}
              />
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="relative flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  进入星狐
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

async function extractDetail(err: unknown): Promise<string> {
  try {
    if (err && typeof err === 'object' && 'response' in err) {
      const res = (err as { response: Response }).response;
      const body = await res.clone().json();
      return body?.detail ?? `HTTP ${res.status}`;
    }
  } catch {
    /* fall through */
  }
  return err instanceof Error ? err.message : '网络异常,请稍后重试';
}
